import React, { useState, useEffect } from "react";
import "./RuleList.css";
import Sidebar from "../../../../Navigation/Sidebar/Sidebar";

const RuleListComponent = (props) => {
  const {
    loading,
    error,
    resetRules = [],
    carryForwardRules = [],
    corporateName = "Corporate",
    departmentName = "",
    ruleStatus = "active",
    showDeleteModal = false,
    ruleToDelete = null,
    deleteRuleType = null,
    deleteDepartmentId = null,
    isDeleting = false,
    onBackClick,
    onAddRuleClick,
    onEditRuleClick,
    onDeleteRuleClick,
    onStatusChange,
    onToggleRuleStatus,
    onCancelDelete,
    onConfirmDelete,
    onRefreshRules
  } = props;

  const [userRoleId, setUserRoleId] = useState(null);

  useEffect(() => {
    try {
      const userData = JSON.parse(localStorage.getItem("data"));
      if (userData && userData.details && userData.details.userRole) {
        setUserRoleId(userData.details.userRole.role_id);
      }
    } catch (error) {
      console.error("Error parsing user data from localStorage:", error);
    }
  }, []);
  
  const canManageRules = userRoleId !== 15 && userRoleId !== null;

  const handleBackClick = () => {
    if (onBackClick) onBackClick();
  };

  const handleAddRule = () => {
    if (onAddRuleClick) onAddRuleClick();
  };

  const handleEditRule = (ruleId, departmentId, departmentName, ruleType) => {
    if (onEditRuleClick) onEditRuleClick(ruleId, departmentId, departmentName, ruleType);
  };

  const handleDeleteRule = (ruleId, ruleType, departmentId) => {
    if (onDeleteRuleClick) onDeleteRuleClick(ruleId, ruleType, departmentId);
  };

  const handleToggleStatus = (ruleId, ruleType, currentStatus) => {
    if (onToggleRuleStatus) onToggleRuleStatus(ruleId, ruleType, currentStatus);
  };

  const handleStatusChange = (status) => {
    if (onStatusChange) onStatusChange(status);
  };

  const handleRefresh = () => {
    if (onRefreshRules) onRefreshRules();
  };

  const filterRulesByStatus = (rules) => {
    if (ruleStatus === 'active') {
      return rules.filter(rule => rule.isActive === true);
    } else if (ruleStatus === 'inactive') {
      return rules.filter(rule => rule.isActive === false);
    }
    return rules;
  };

  const filteredResetRules = filterRulesByStatus(resetRules);
  const filteredCarryForwardRules = filterRulesByStatus(carryForwardRules);

  const totalRules = resetRules.length + carryForwardRules.length;
  const activeRules = resetRules.filter(r => r.isActive === true).length + carryForwardRules.filter(r => r.isActive === true).length;
  const inactiveRules = totalRules - activeRules;

  const getStatusDisplay = (rule) => {
    return rule.isActive ? 'Active' : 'Inactive';
  };

  const shouldShowActions = (rule) => {
    if (!canManageRules) return false;
    if (ruleStatus === 'active') {
      return true;
    } else if (ruleStatus === 'all') {
      return rule.isActive === true;
    }
    return false;
  };

  const renderActionButtons = (rule, ruleType) => {
    if (!shouldShowActions(rule)) {
      return null;
    }

    return (
      <div className="action-buttons">
        <button 
          className="action-btn edit-btn"
          onClick={() => handleEditRule(rule.ruleId, rule.departmentId, rule.department, ruleType)}
          title={`Edit ${ruleType === 'reset' ? 'Reset' : 'Carry Forward'} Rule`}
        >
          Edit
        </button>
        <button 
          className="action-btn delete-btn"
          onClick={() => handleDeleteRule(rule.ruleId, ruleType, rule.departmentId)}
          title="Delete Rule"
        >
          Delete
        </button>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="rule-list-container">
        <div className="sidebar-container">
          <Sidebar />
        </div>
        <div className="rule-content">
          <div className="rule-breadcrumb">
            <a href="/">Corporate Account</a> &gt; <span>Rule Details</span>
          </div>
          <div className="deprt-loading-state">
            <div className="deprt-loading-spinner"></div>
            <p className="deprt-loading-text">Loading rules...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rule-list-container">
        <div className="sidebar-container">
          <Sidebar />
        </div>
        <div className="rule-content">
          <div className="rule-header">
            <div className="rule-header-content">
              <div className="rule-title-section">
                <button className="nav-back-btn" onClick={handleBackClick}>
                  ←
                </button>
                <div className="rule-titles">
                  <h1 className="rule-title">
                    Corporate Rules for <span className="corporate-name-highlight">{corporateName}</span>
                  </h1>
                </div>
              </div>
            </div>
          </div>
          <div className="error-state">
            <div className="error-icon">⚠️</div>
            <h3 className="error-title">Error Loading Rules</h3>
            <button className="retry-btn" onClick={handleRefresh}>
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rule-list-container">
      <div className="sidebar-container">
        <Sidebar />
      </div>

      <div className="rule-content">
        <div className="rule-header">
          <div className="rule-header-content">
            <div className="rule-title-section">
              <button className="nav-back-btn" onClick={handleBackClick}>
                ←
              </button>
              <div className="rule-titles">
                <h1 className="rule-title">
                  Corporate Rules for <span className="corporate-name-highlight">{corporateName}</span>
                </h1>
              </div>
            </div>
            
            <div className="header-controls">
              <div className="rule-status-filter">
                <button 
                  className={`status-option ${ruleStatus === 'all' ? 'active' : ''}`}
                  onClick={() => handleStatusChange('all')}
                >
                  All ({totalRules})
                </button>
                <button 
                  className={`status-option ${ruleStatus === 'active' ? 'active' : ''}`}
                  onClick={() => handleStatusChange('active')}
                >
                  Active ({activeRules})
                </button>
                <button 
                  className={`status-option ${ruleStatus === 'inactive' ? 'active' : ''}`}
                  onClick={() => handleStatusChange('inactive')}
                >
                  Inactive ({inactiveRules})
                </button>
              </div>
              
              {canManageRules && ruleStatus !== 'inactive' && (
                <button className="add-rule-btn" onClick={handleAddRule}>
                  <span>+</span>
                  Add New Rule
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="rule-scroll-content">
          <div className="active-rules-section">
            {/* Reset Type Rules */}
            <div className="rule-type-card">
              <div className="rule-type-header">
                <h3 className="rule-type-title">Reset Type Rules</h3>
                <div className="rule-type-info">
                  <span className="rule-count-badge">
                    {filteredResetRules.length} {ruleStatus === 'all' ? 'Total' : ruleStatus}
                  </span>
                </div>
              </div>
              
              <div className="rule-table-scroll-container">
                {filteredResetRules.length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-state-icon">📋</div>
                    <h3 className="empty-state-title">No Reset Type Rules Found</h3>
                    <p className="empty-state-description">
                      {ruleStatus === 'all' 
                        ? 'No reset type rules have been created yet.'
                        : `No ${ruleStatus} reset type rules found.`}
                    </p>
                  </div>
                ) : (
                  <table className="rule-table">
                    <thead>
                      <tr>
                        <th>RULE ID</th>
                        <th>DEPARTMENT</th>
                        <th>AMOUNT</th>
                        <th>FREQUENCY</th>
                        <th>RESET ON</th>
                        <th>STATUS</th>
                        {canManageRules && ruleStatus !== 'inactive' && <th>ACTIONS</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredResetRules.map((rule) => (
                        <tr key={rule.ruleId}>
                          <td className="rule-id-cell">{rule.ruleId}</td>
                          <td className="department-cell">
                            <div>
                              <div className="dept-name">{rule.department}</div>
                              <div className="dept-id">ID: {rule.departmentId}</div>
                            </div>
                          </td>
                          <td className="amount-cell">{rule.amount}</td>
                          <td>
                            <div className="frequency-cell">
                              <span className="frequency-badge">{rule.frequency}</span>
                            </div>
                          </td>
                          <td className="date-cell">{rule.resetOn}</td>
                          <td className="status-cell">
                            <span className={`corp-rule-status-badge ${rule.isActive ? 'corp-status-active' : 'corp-status-inactive'}`}>
                              {getStatusDisplay(rule)}
                            </span>
                          </td>
                          {canManageRules && ruleStatus !== 'inactive' && (
                            <td className="action-cell">
                              {renderActionButtons(rule, 'reset')}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            {/* Carry Forward Type Rules */}
            <div className="rule-type-card">
              <div className="rule-type-header">
                <h3 className="rule-type-title">Carry Forward Type Rules</h3>
                <div className="rule-type-info">
                  <span className="rule-count-badge">
                    {filteredCarryForwardRules.length} {ruleStatus === 'all' ? 'Total' : ruleStatus}
                  </span>
                </div>
              </div>
              
              <div className="rule-table-scroll-container">
                {filteredCarryForwardRules.length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-state-icon">📋</div>
                    <h3 className="empty-state-title">No Carry Forward Rules Found</h3>
                    <p className="empty-state-description">
                      {ruleStatus === 'all' 
                        ? 'No carry forward type rules have been created yet.'
                        : `No ${ruleStatus} carry forward type rules found.`}
                    </p>
                  </div>
                ) : (
                  <table className="rule-table">
                    <thead>
                      <tr>
                        <th>RULE ID</th>
                        <th>DEPARTMENT</th>
                        <th>AMOUNT</th>
                        <th>MIN. AMOUNT</th>
                        <th>FREQUENCY</th>
                        <th>CARRY FORWARD ON</th>
                        <th>STATUS</th>
                        {canManageRules && ruleStatus !== 'inactive' && <th>ACTIONS</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCarryForwardRules.map((rule) => (
                        <tr key={rule.ruleId}>
                          <td className="rule-id-cell">{rule.ruleId}</td>
                          <td className="department-cell">
                            <div>
                              <div className="dept-name">{rule.department}</div>
                              <div className="dept-id">ID: {rule.departmentId}</div>
                            </div>
                          </td>
                          <td className="amount-cell">{rule.amount}</td>
                          <td className="amount-cell">{rule.minRequest || "N/A"}</td>
                          <td>
                            <div className="frequency-cell">
                              <span className="frequency-badge">{rule.frequency}</span>
                            </div>
                          </td>
                          <td className="date-cell">{rule.carryForwardOn}</td>
                          <td className="status-cell">
                            <span className={`corp-rule-status-badge ${rule.isActive ? 'corp-status-active' : 'corp-status-inactive'}`}>
                              {getStatusDisplay(rule)}
                            </span>
                          </td>
                          {canManageRules && ruleStatus !== 'inactive' && (
                            <td className="action-cell">
                              {renderActionButtons(rule, 'carry-forward')}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteModal && ruleToDelete && (
          <div className="delete-modal-overlay">
            <div className="delete-modal">
              <div className="delete-modal-header">
                <h3 className="delete-modal-title">Delete Rule</h3>
              </div>
              <div className="delete-modal-content">
                <p>Are you sure you want to delete this <strong>{deleteRuleType === 'reset' ? 'Reset' : 'Carry Forward'}</strong> rule?</p>
                <div className="rule-details-box">
                  <div className="detail-row">
                    <span className="detail-label">Rule ID:</span>
                    <span className="detail-value">{ruleToDelete}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Department ID:</span>
                    <span className="detail-value">
                      {deleteDepartmentId !== undefined && deleteDepartmentId !== null 
                        ? deleteDepartmentId 
                        : 'N/A'}
                    </span>
                  </div>
                </div>
                <p className="delete-warning">
                  ⚠️ This action cannot be undone. The rule will be permanently deleted.
                </p>
              </div>
              <div className="delete-modal-actions">
                <button 
                  className="cancel-btn" 
                  onClick={onCancelDelete}
                  disabled={isDeleting}
                >
                  Cancel
                </button>
                <button 
                  className="confirm-delete-btn" 
                  onClick={onConfirmDelete}
                  disabled={isDeleting}
                >
                  {isDeleting ? (
                    <>
                      <span className="spinner-small"></span>
                      Deleting...
                    </>
                  ) : (
                    'Delete Rule'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RuleListComponent;